import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtPrice, mediaUrl, apiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { ProductForm } from "@/components/ProductForm";
import { statusLabel, statusColor, STATUS_FLOW } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { Package, ShoppingCart, TrendingUp, Wallet, Plus, Pencil, Trash2, Loader2, Store, Upload, ExternalLink, Image as ImageIcon, Rocket } from "lucide-react";
import { toast } from "sonner";

export default function SellerDashboard() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("products");

  const load = useCallback(async () => {
    const me = await api.get("/auth/me");
    setUser(me.data);
    const [s, o] = await Promise.all([api.get("/seller/stats"), api.get("/seller/orders")]);
    setStats(s.data); setOrders(o.data);
    const p = await api.get(`/seller/products`);
    setProducts(p.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success(t("delete")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const advance = async (order, status) => {
    try { await api.put(`/orders/${order.id}/status`, { status }); toast.success(statusLabel(t, status)); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const nextSellerStatus = (s) => {
    const map = { placed: "confirmed", confirmed: "preparing", preparing: "shipped" };
    return map[s];
  };

  const statCards = [
    { icon: Package, k: "stats_products", v: stats?.products },
    { icon: TrendingUp, k: "stats_active", v: stats?.active },
    { icon: ShoppingCart, k: "stats_orders", v: stats?.orders },
    { icon: Wallet, k: "stats_revenue", v: stats ? fmtPrice(stats.revenue) : "—" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <h1 className="font-display font-extrabold text-3xl tracking-tight">{t("seller_dashboard")}</h1>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-full bg-mango-600 hover:bg-mango-700" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" />{t("add_product")}</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {statCards.map(({ icon: Icon, k, v }) => (
          <div key={k} className="bg-white border border-stone-200 rounded-xl p-5">
            <Icon className="w-6 h-6 text-emerald-800 mb-3" />
            <p className="text-2xl font-display font-extrabold text-stone-900">{v ?? "—"}</p>
            <p className="text-xs text-stone-500 uppercase tracking-wider mt-1">{t(k)}</p>
          </div>
        ))}
      </div>

      {stats && products.length === 0 && (
        <div className="bg-emerald-800 text-white rounded-2xl p-6 sm:p-8 mb-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between" data-testid="seller-onboarding">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/15 grid place-items-center shrink-0"><Rocket className="w-6 h-6" /></div>
            <div>
              <h2 className="font-display font-extrabold text-xl sm:text-2xl">{t("onboard_title")}</h2>
              <p className="text-emerald-100 text-sm mt-1 max-w-xl">{t("onboard_desc")}</p>
            </div>
          </div>
          <div className="flex gap-3 shrink-0">
            <Button onClick={() => setTab("shop")} variant="outline" className="rounded-full bg-transparent text-white border-white/50 hover:bg-white/10 hover:text-white" data-testid="onboard-setup-shop"><Store className="w-4 h-4 mr-1" />{t("onboard_setup_shop")}</Button>
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-full bg-mango-600 hover:bg-mango-700" data-testid="onboard-add-product"><Plus className="w-4 h-4 mr-1" />{t("add_product")}</Button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList><TabsTrigger value="products" data-testid="tab-products">{t("my_products")}</TabsTrigger><TabsTrigger value="orders" data-testid="tab-orders">{t("sales_orders")}</TabsTrigger><TabsTrigger value="shop" data-testid="tab-shop">{t("my_shop")}</TabsTrigger></TabsList>

        <TabsContent value="products" className="mt-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid={`seller-product-${p.id}`}>
                <img src={mediaUrl(p.image)} alt={p.name} className="w-full h-40 object-cover bg-stone-100" />
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium line-clamp-1">{p.name}</p>
                    {!p.approved && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">{t("pending_approval")}</span>}
                  </div>
                  <p className="font-display font-bold text-emerald-800 mt-1">{fmtPrice(p.price)}</p>
                  <p className="text-xs text-stone-400">{t("stock")}: {p.stock}</p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditing(p); setFormOpen(true); }} data-testid={`edit-${p.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />{t("edit")}</Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => del(p.id)} data-testid={`delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
            {products.length === 0 && <p className="text-stone-400 col-span-full py-10 text-center">{t("no_results")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-5">
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-stone-200 rounded-xl p-5" data-testid={`seller-order-${o.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div><p className="font-display font-bold">{o.order_no}</p><p className="text-xs text-stone-400">{o.buyer_name} · {new Date(o.created_at).toLocaleDateString()}</p></div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusColor(o.status)}`}>{statusLabel(t, o.status)}</span>
                    {nextSellerStatus(o.status) && <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900 h-8" onClick={() => advance(o, nextSellerStatus(o.status))} data-testid={`advance-${o.id}`}>
                      {o.status === "placed" ? t("confirmed_next") : o.status === "confirmed" ? t("status_preparing") : t("ship_it")}
                    </Button>}
                  </div>
                </div>
                <div className="mt-3 text-sm text-stone-600">{o.items.filter((i) => user && i.seller_id === user.id).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
              </div>
            ))}
            {orders.length === 0 && <p className="text-stone-400 py-10 text-center">{t("no_orders")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="shop" className="mt-5">
          <ShopSettings user={user} onSaved={load} />
        </TabsContent>
      </Tabs>

      <ProductForm open={formOpen} setOpen={setFormOpen} product={editing} onSaved={load} />
    </div>
  );
}

function ShopSettings({ user, onSaved }) {
  const { t } = useLang();
  const fileRef = useRef();
  const coverRef = useRef();
  const [form, setForm] = useState({ shop_name: "", shop_bio: "", shop_logo: "", shop_cover: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (user) setForm({ shop_name: user.shop_name || user.name || "", shop_bio: user.shop_bio || "", shop_logo: user.shop_logo || user.avatar || "", shop_cover: user.shop_cover || "" });
  }, [user]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("shop_logo", data.url);
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("shop_cover", data.url);
    } catch (err) { toast.error(apiError(err)); } finally { setUploadingCover(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/seller/profile", form);
      toast.success(t("save_shop"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-6 max-w-2xl" data-testid="shop-settings">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Store className="w-5 h-5 text-emerald-800" /> {t("my_shop")}</h3>
        {user && <Link to={`/shop/${user.id}`} className="text-sm text-emerald-800 font-medium flex items-center gap-1 hover:text-mango-600" data-testid="visit-shop-link">{t("visit_shop")} <ExternalLink className="w-3.5 h-3.5" /></Link>}
      </div>
      <div className="mb-5" data-testid="shop-cover-editor">
        <p className="text-xs font-medium text-stone-600 mb-2">{t("shop_cover_label")}</p>
        <div className="relative h-32 rounded-xl overflow-hidden bg-stone-100 border grid place-items-center">
          {form.shop_cover ? <img src={mediaUrl(form.shop_cover)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-stone-300" />}
          <input ref={coverRef} type="file" accept="image/*" onChange={uploadCover} className="hidden" data-testid="shop-cover-input" />
          <Button type="button" size="sm" variant="secondary" onClick={() => coverRef.current?.click()} disabled={uploadingCover} className="relative z-10 bg-white/90 hover:bg-white" data-testid="shop-cover-btn">
            {uploadingCover ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}{t("shop_cover_label")}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-4 mb-5">
        <div className="w-20 h-20 rounded-2xl bg-stone-100 border grid place-items-center overflow-hidden">
          {form.shop_logo ? <img src={mediaUrl(form.shop_logo)} alt="" className="w-full h-full object-cover" /> : <Store className="w-8 h-8 text-stone-300" />}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="shop-logo-input" />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="shop-logo-btn">
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}{t("shop_logo_label")}
        </Button>
      </div>
      <div className="space-y-4">
        <div><Label className="text-xs">{t("shop_name_label")}</Label><Input value={form.shop_name} onChange={(e) => upd("shop_name", e.target.value)} data-testid="shop-name-input" /></div>
        <div><Label className="text-xs">{t("shop_bio_label")}</Label><Textarea rows={4} value={form.shop_bio} onChange={(e) => upd("shop_bio", e.target.value)} data-testid="shop-bio-input" /></div>
        <Button onClick={save} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900" data-testid="save-shop-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("save_shop")}</Button>
      </div>
    </div>
  );
}
